import React, { useState, useRef, useEffect } from 'react';
import './RevisionRequestModal.css';
import { X, Send, Clock, FileText, Plus, Trash2 } from 'lucide-react';

const RevisionRequestModal = ({ isOpen, onClose, submission, selectedVideos, onSubmit }) => {
  const [revisionData, setRevisionData] = useState({});
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [rangeDuration, setRangeDuration] = useState(null); // Duration of the restricted range
  const videoRef = useRef(null);

  const currentVideo = selectedVideos?.[currentVideoIndex];
  
  // Reset range duration when video changes
  useEffect(() => {
    if (isOpen && currentVideo) {
      setRangeDuration(null);
      setCurrentTime(0);
    }
  }, [isOpen, currentVideoIndex, currentVideo?.id]);

  if (!isOpen) return null;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      let currentVideoTime = video.currentTime;
      
      // Get time range - check both direct fields and metadata object
      const variationStartTime = currentVideo?.variationStartTime || currentVideo?.metadata?.variationStartTime;
      const variationEndTime = currentVideo?.variationEndTime || currentVideo?.metadata?.variationEndTime;
      const videoType = currentVideo?.type || currentVideo?.variationType;
      
      let rangeStart = 0;
      let rangeEnd = video.duration || 0;
      let hasTimeRange = false;
      
      // Check if we have time range data (either variationEndTime for hooks or variationStartTime for CTAs)
      // For hooks: start at 0, end at variationEndTime
      if (videoType === 'hook' && variationEndTime != null && variationEndTime !== '') {
        rangeStart = 0;
        rangeEnd = parseFloat(variationEndTime) || video.duration || 0;
        hasTimeRange = true;
        console.log('🎣 Hook detected - restricting to range:', { rangeStart, rangeEnd, variationEndTime, videoType });
      } 
      // For CTAs: start at variationStartTime, end at video end
      else if (videoType === 'cta' && variationStartTime != null && variationStartTime !== '') {
        rangeStart = parseFloat(variationStartTime) || 0;
        rangeEnd = video.duration || 0;
        hasTimeRange = true;
        console.log('📢 CTA detected - restricting to range:', { rangeStart, rangeEnd, variationStartTime, videoType });
      }
      // Also check if we have time range data even without explicit type (fallback)
      else if (variationEndTime != null && variationEndTime !== '' && variationEndTime < video.duration) {
        // Likely a hook (ends before video end)
        rangeStart = 0;
        rangeEnd = parseFloat(variationEndTime) || video.duration || 0;
        hasTimeRange = true;
        console.log('🎣 Time range detected (likely hook) - restricting to range:', { rangeStart, rangeEnd, variationEndTime });
      }
      else if (variationStartTime != null && variationStartTime !== '' && parseFloat(variationStartTime) > 0) {
        // Likely a CTA (starts after video start)
        rangeStart = parseFloat(variationStartTime) || 0;
        rangeEnd = video.duration || 0;
        hasTimeRange = true;
        console.log('📢 Time range detected (likely CTA) - restricting to range:', { rangeStart, rangeEnd, variationStartTime });
      }
      
      // Only restrict if we have a time range defined
      if (hasTimeRange) {
        // If video is outside the range, loop it back to the start of the range
        if (currentVideoTime < rangeStart) {
          video.currentTime = rangeStart;
          currentVideoTime = rangeStart;
        } else if (currentVideoTime >= rangeEnd && rangeEnd > 0 && rangeEnd < video.duration) {
          // Loop back to start when reaching the end of the range (only if rangeEnd is less than full duration)
          video.currentTime = rangeStart;
          currentVideoTime = rangeStart;
          // If playing, continue playing from the start
          if (!video.paused) {
            video.play();
          }
        }
        
        // Store relative time (from range start) for display
        const relativeTime = Math.max(0, currentVideoTime - rangeStart);
        const calculatedRangeDuration = rangeEnd - rangeStart;
        setCurrentTime(relativeTime);
        setRangeDuration(calculatedRangeDuration);
      } else {
        // No time range restriction - use absolute time
        console.log('ℹ️ No time range restriction - showing full video:', {
          videoType,
          variationStartTime,
          variationEndTime,
          currentVideo: currentVideo ? Object.keys(currentVideo) : 'null'
        });
        setCurrentTime(currentVideoTime);
        setRangeDuration(null); // No range restriction
      }
    }
  };

  const updateVideoRevision = (videoId, field, value) => {
    setRevisionData(prev => ({
      ...prev,
      [videoId]: {
        ...prev[videoId],
        [field]: value
      }
    }));
  };

  const addTimestampedNote = (videoId) => {
    // Pause the video
    if (videoRef.current) {
      videoRef.current.pause();
    }
    
    // currentTime is already relative to range start, so use it directly
    const currentNotes = revisionData[videoId]?.timestampedNotes || [];
    updateVideoRevision(videoId, 'timestampedNotes', [
      ...currentNotes,
      { timestamp: formatTime(currentTime), note: '', relativeTime: currentTime }
    ]);
  };

  const updateTimestampedNote = (videoId, index, field, value) => {
    const currentNotes = revisionData[videoId]?.timestampedNotes || [];
    const updatedNotes = currentNotes.map((note, i) => 
      i === index ? { ...note, [field]: value } : note
    );
    updateVideoRevision(videoId, 'timestampedNotes', updatedNotes);
  };

  const removeTimestampedNote = (videoId, index) => {
    const currentNotes = revisionData[videoId]?.timestampedNotes || [];
    const updatedNotes = currentNotes.filter((_, i) => i !== index);
    updateVideoRevision(videoId, 'timestampedNotes', updatedNotes);
  };

  const handleSubmit = () => {
    // If there are multiple videos and we're not on the last one, go to next video
    if (selectedVideos.length > 1 && currentVideoIndex < selectedVideos.length - 1) {
      setCurrentVideoIndex(currentVideoIndex + 1);
      return;
    }
    
    // If this is the last video (or only video), submit the revision request
    // Keep general notes per video (don't combine them)
    const videoGeneralNotes = {};
    selectedVideos.forEach(video => {
      videoGeneralNotes[video.id] = revisionData[video.id]?.generalNotes || '';
    });
    
    const allTimestampedFeedback = [];
    selectedVideos.forEach(video => {
      const notes = revisionData[video.id]?.timestampedNotes || [];
      // Get time range - check both direct fields and metadata object
      const variationStartTime = video?.variationStartTime || video?.metadata?.variationStartTime;
      const variationEndTime = video?.variationEndTime || video?.metadata?.variationEndTime;
      const videoType = video?.type || video?.variationType;
      let rangeStart = 0;
      
      // Calculate range start for converting relative time to absolute
      if (videoType === 'hook' && variationEndTime) {
        rangeStart = 0;
      } else if (videoType === 'cta' && variationStartTime) {
        rangeStart = parseFloat(variationStartTime) || 0;
      }
      
      notes.forEach(note => {
        if (note.note && note.note.trim()) {
          // Convert relative time back to absolute time for storage
          const relativeTime = note.relativeTime || 0;
          const absoluteTime = rangeStart + relativeTime;
          
          allTimestampedFeedback.push({
            videoId: video.id,
            videoTitle: video.title,
            timestamp: note.timestamp, // Keep display timestamp
            absoluteTime: absoluteTime, // Store absolute time for reference
            relativeTime: relativeTime, // Store relative time
            note: note.note,
            type: 'revision'
          });
        }
      });
    });
    
    // For backward compatibility, also include combined generalFeedback
    // But the per-video notes should be used instead
    const allGeneralNotes = selectedVideos
      .map(video => revisionData[video.id]?.generalNotes || '')
      .filter(note => note.trim())
      .join('\n\n');
    
    const revisionRequest = {
      submissionId: submission.id,
      selectedVideos: selectedVideos,  // Send as selectedVideos for BrandFeedbackModal
      generalFeedback: allGeneralNotes, // Keep for backward compatibility
      videoGeneralNotes: videoGeneralNotes, // NEW: Per-video general notes
      timestampedFeedback: allTimestampedFeedback,
      submittedAt: new Date()
    };
    
    onSubmit(revisionRequest);
    onClose();
  };

  const isFormValid = () => {
    // For single video, require at least one note
    if (selectedVideos.length === 1) {
      const data = revisionData[currentVideo.id];
      return data?.generalNotes?.trim() || 
             (data?.timestampedNotes && data.timestampedNotes.length > 0 && 
              data.timestampedNotes.some(note => note.note?.trim()));
    }
    
    // For multiple videos, only require note for current video to proceed
    const data = revisionData[currentVideo.id];
    return data?.generalNotes?.trim() || 
           (data?.timestampedNotes && data.timestampedNotes.length > 0 && 
            data.timestampedNotes.some(note => note.note?.trim()));
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="revision-request-modal-overlay" onClick={handleOverlayClick}>
      <div className="revision-request-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="revision-request-modal-header">
          <div className="revision-request-modal-title-section">
            <div className="revision-request-creator-info">
              <div className="revision-request-creator-avatar">
                <img 
                  src={submission.creator.avatar} 
                  alt={submission.creator.name}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="revision-request-avatar-fallback">
                  {submission.creator.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </div>
              </div>
                   <div className="revision-request-creator-details">
                     <div className="revision-request-title-row">
                       <h3 className="revision-request-modal-title">Request Revision</h3>
                       {selectedVideos.length > 1 && (
                         <div className="revision-request-progress-indicator">
                           {currentVideoIndex + 1} of {selectedVideos.length}
                         </div>
                       )}
                     </div>
                     <p className="revision-request-modal-subtitle">
                       {submission.creator.name} - {submission.campaign}
                     </p>
                     <p className="revision-request-video-name">
                       {currentVideo.title}
                     </p>
                   </div>
            </div>
          </div>
          <button className="revision-request-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="revision-request-modal-content">
          <div className="revision-request-review-section">
            {/* Video Section */}
            <div className="revision-request-video-section">
              <div className="revision-request-video-player">
                <video
                  ref={videoRef}
                  src={currentVideo.downloadURL || currentVideo.videoUrl}
                  controls
                  className="revision-request-video"
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={() => {
                    // Set video to start of range when metadata loads
                    if (videoRef.current) {
                      const video = videoRef.current;
                      // Get time range - check both direct fields and metadata object
                      const variationStartTime = currentVideo?.variationStartTime || currentVideo?.metadata?.variationStartTime;
                      const variationEndTime = currentVideo?.variationEndTime || currentVideo?.metadata?.variationEndTime;
                      const videoType = currentVideo?.type || currentVideo?.variationType;
                      
                      console.log('📹 Video loaded - checking time range:', {
                        videoType,
                        variationStartTime,
                        variationEndTime,
                        currentVideo: {
                          type: currentVideo?.type,
                          variationType: currentVideo?.variationType,
                          variationStartTime: currentVideo?.variationStartTime,
                          variationEndTime: currentVideo?.variationEndTime,
                          metadata: currentVideo?.metadata
                        }
                      });
                      
                      let rangeStart = 0;
                      let hasTimeRange = false;
                      
                      // For hooks: start at 0
                      if (videoType === 'hook' && variationEndTime) {
                        rangeStart = 0;
                        hasTimeRange = true;
                        console.log('🎣 Setting hook range start to 0');
                      } 
                      // For CTAs: start at variationStartTime
                      else if (videoType === 'cta' && variationStartTime) {
                        rangeStart = parseFloat(variationStartTime) || 0;
                        hasTimeRange = true;
                        console.log('📢 Setting CTA range start to:', rangeStart);
                      }
                      
                      if (hasTimeRange && video.readyState >= 2) {
                        video.currentTime = rangeStart;
                        console.log('✅ Video set to range start:', rangeStart);
                      }
                    }
                  }}
                  onSeeked={() => {
                    // Ensure video stays within range when seeking
                    if (videoRef.current) {
                      const video = videoRef.current;
                      // Get time range - check both direct fields and metadata object
                      const variationStartTime = currentVideo?.variationStartTime || currentVideo?.metadata?.variationStartTime;
                      const variationEndTime = currentVideo?.variationEndTime || currentVideo?.metadata?.variationEndTime;
                      const videoType = currentVideo?.type || currentVideo?.variationType;
                      let rangeStart = 0;
                      let rangeEnd = video.duration || 0;
                      let hasTimeRange = false;
                      
                      // For hooks: start at 0, end at variationEndTime
                      if (videoType === 'hook' && variationEndTime) {
                        rangeStart = 0;
                        rangeEnd = parseFloat(variationEndTime) || video.duration || 0;
                        hasTimeRange = true;
                      } 
                      // For CTAs: start at variationStartTime, end at video end
                      else if (videoType === 'cta' && variationStartTime) {
                        rangeStart = parseFloat(variationStartTime) || 0;
                        rangeEnd = video.duration || 0;
                        hasTimeRange = true;
                      }
                      
                      if (hasTimeRange) {
                        if (video.currentTime < rangeStart) {
                          console.log('⚠️ Seeked before range start, correcting to:', rangeStart);
                          video.currentTime = rangeStart;
                        } else if (video.currentTime > rangeEnd && rangeEnd > 0) {
                          console.log('⚠️ Seeked after range end, correcting to:', rangeStart);
                          video.currentTime = rangeStart;
                        }
                      }
                    }
                  }}
                />
              </div>
              
              <div className="revision-request-video-controls">
                <div className="revision-request-controls-pill">
                  <div className="revision-request-current-time">
                    <Clock size={14} />
                    {rangeDuration !== null 
                      ? `${formatTime(currentTime)} / ${formatTime(rangeDuration)}`
                      : formatTime(currentTime)}
                  </div>
                  <button 
                    className="revision-request-add-timestamp-btn"
                    onClick={() => addTimestampedNote(currentVideo.id)}
                  >
                    <Plus size={16} />
                    Add Note
                  </button>
                </div>
              </div>
            </div>

            {/* Feedback Section */}
            <div className="revision-request-feedback-section">
              <div className="revision-request-timestamped-notes">
                <h3>Timestamped Feedback</h3>
                {(!revisionData[currentVideo.id]?.timestampedNotes || revisionData[currentVideo.id].timestampedNotes.length === 0) ? (
                  <div className="revision-request-no-notes">
                    <p>Click "Add Note" while playing the video to add timestamped feedback</p>
                  </div>
                ) : (
                  <div className="revision-request-notes-list">
                    {revisionData[currentVideo.id].timestampedNotes.map((note, index) => (
                      <div key={index} className="revision-request-timestamped-note">
                        <div className="revision-request-note-time">{note.timestamp || '0:00'}</div>
                        <textarea
                          value={note.note}
                          onChange={(e) => updateTimestampedNote(currentVideo.id, index, 'note', e.target.value)}
                          placeholder="Add a note about this moment..."
                          className="revision-request-note-input"
                          rows={3}
                        />
                        <button 
                          className="revision-request-remove-note-btn"
                          onClick={() => removeTimestampedNote(currentVideo.id, index)}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="revision-request-general-feedback">
                <h3>General Feedback</h3>
                <textarea
                  value={revisionData[currentVideo.id]?.generalNotes || ''}
                  onChange={(e) => updateVideoRevision(currentVideo.id, 'generalNotes', e.target.value)}
                  placeholder="Add overall feedback and general revision requests..."
                  className="revision-request-general-feedback-textarea"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="revision-request-modal-actions">
          <button 
            className="revision-request-cancel-btn"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="revision-request-submit-btn"
            onClick={handleSubmit}
            disabled={!isFormValid()}
          >
            {selectedVideos.length > 1 && currentVideoIndex < selectedVideos.length - 1 
              ? `Next Video (${currentVideoIndex + 2}/${selectedVideos.length})`
              : 'Submit Revision Request'
            }
          </button>
        </div>
      </div>
    </div>
  );
};

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export default RevisionRequestModal;

